import React, { useContext, useMemo } from 'react';
import { TableCell, Box, Chip, Tooltip, Stack, Typography, useTheme, alpha } from '@mui/material';
import { Worker } from '../../../models/worker';
import { SettingsContext } from '../../../context/SettingsContext';

const normalizeHex = (raw?: string | null): string | null => {
    const trimmed = raw?.trim();
    if (!trimmed) return null;
    return (trimmed.startsWith('#') ? trimmed : `#${trimmed}`).toUpperCase();
};

/** Profession(s): custom occupation, multi-profession chips, or a single legacy chip. */
const WorkerProfessionCell: React.FC<{ worker: Worker }> = ({ worker }) => {
    const theme = useTheme();
    const { settings } = useContext(SettingsContext);

    const professionAccentColor = useMemo(() => {
        if (!worker.professionKey) return null;
        const match = settings?.professions?.find((p) => p.professionKey === worker.professionKey);
        return normalizeHex(match?.colorHex);
    }, [settings?.professions, worker.professionKey]);

    const professionChipStyles = useMemo(() => {
        const resolvedColor = professionAccentColor || theme.palette.primary.main;
        return {
            borderColor: alpha(resolvedColor, professionAccentColor ? 0.5 : 0.3),
            color: resolvedColor,
            fontWeight: 600,
            backgroundColor: professionAccentColor ? alpha(resolvedColor, 0.08) : 'transparent',
            '& .MuiChip-label': { px: 1.25 },
        };
    }, [professionAccentColor, theme]);

    const professionDotStyles = useMemo(() => {
        const resolvedColor = professionAccentColor || theme.palette.primary.main;
        return {
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: resolvedColor,
            boxShadow: `0 0 0 2px ${alpha(resolvedColor, 0.2)}`,
        };
    }, [professionAccentColor, theme]);

    const professionAccentTitle = professionAccentColor
        ? `Accent color ${professionAccentColor}`
        : 'Default profession color';

    return (
        <TableCell>
            {worker.isCustomOccupation && worker.customOccupation ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        label={worker.customOccupation}
                        size="small"
                        variant="filled"
                        sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.dark }}
                    />
                    <Tooltip title="Custom occupation">
                        <Typography variant="caption" sx={{ color: theme.palette.secondary.main, fontStyle: 'italic' }}>
                            (Custom)
                        </Typography>
                    </Tooltip>
                </Box>
            ) : worker.professions && worker.professions.some((p) => p.isActive) ? (
                // Primary chip first (highlighted), then secondary chips; colors from settings colorHex.
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                    <Box sx={professionDotStyles} />
                    {worker.professions
                        .filter((p) => p.isActive)
                        .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
                        .map((p) => {
                            const settingsMatch = settings?.professions?.find((s) => s.professionKey === p.professionKey);
                            const accent = normalizeHex(settingsMatch?.colorHex) || theme.palette.primary.main;
                            const tooltip = [p.isPrimary ? 'Primary' : 'Secondary', `Added via ${p.addedVia}`].join(' · ');
                            return (
                                <Tooltip key={p.professionKey} title={tooltip}>
                                    <Chip
                                        label={p.professionKey}
                                        size="small"
                                        variant={p.isPrimary ? 'filled' : 'outlined'}
                                        sx={{
                                            borderColor: alpha(accent, p.isPrimary ? 0.7 : 0.4),
                                            color: p.isPrimary ? '#fff' : accent,
                                            backgroundColor: p.isPrimary ? alpha(accent, 0.85) : alpha(accent, 0.08),
                                            fontWeight: p.isPrimary ? 700 : 500,
                                            '& .MuiChip-label': { px: 1 },
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                </Stack>
            ) : worker.professionKey ? (
                // Legacy fallback for rows without professions[] populated.
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={professionDotStyles} />
                    <Tooltip title={professionAccentTitle}>
                        <Chip label={worker.professionKey} size="small" variant="outlined" sx={professionChipStyles} />
                    </Tooltip>
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary">Not specified</Typography>
            )}
        </TableCell>
    );
};

export default WorkerProfessionCell;
